import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ToastContainer, toast, Bounce } from "react-toastify";
import { FaSpinner } from "react-icons/fa";
import { Checkbox, Collapse } from "antd";
import rbacService from "../../services/rbacService";
import { PERMISSION_GROUPS, PERMISSION_MODULE_NAMES } from "../../constants/permissions";

const { Panel } = Collapse;

const RoleForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const existingRole = location.state?.role;

  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPermissions, setLoadingPermissions] = useState(true);
  const [loadingRolePermissions, setLoadingRolePermissions] = useState(
    Boolean(existingRole)
  );

  useEffect(() => {
    // Load all available permissions
    const fetchPermissions = async () => {
      try {
        const permissions = await rbacService.getAllPermissions();
        setAllPermissions(permissions);
      } catch (error) {
        toast.error(
          <p dir="rtl" className="font-custom">
            خطا در بارگذاری دسترسی‌ها
          </p>
        );
      } finally {
        setLoadingPermissions(false);
      }
    };

    fetchPermissions();

    // If editing, load existing role data
    if (existingRole) {
      setLoadingRolePermissions(true);
      setFormData({
        name: existingRole.name,
        description: existingRole.description || "",
      });

      // Load role permissions
      const fetchRolePermissions = async () => {
        try {
          const permissions = await rbacService.getPermissionsByRole(existingRole.id);
          setSelectedPermissions(Array.isArray(permissions) ? permissions : []);
        } catch (error) {
          toast.error(
            <p dir="rtl" className="font-custom">
              خطا در بارگذاری دسترسی‌های نقش
            </p>
          );
        } finally {
          setLoadingRolePermissions(false);
        }
      };

      fetchRolePermissions();
    } else {
      setSelectedPermissions([]);
      setLoadingRolePermissions(false);
    }
  }, [existingRole]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePermissionToggle = (permission) => {
    setSelectedPermissions((prev) =>
      prev.includes(permission)
        ? prev.filter((p) => p !== permission)
        : [...prev, permission]
    );
  };

  const handleModuleToggle = (modulePermissions) => {
    const allSelected = modulePermissions.every((p) =>
      selectedPermissions.includes(p.key)
    );

    if (allSelected) {
      // Unselect all
      setSelectedPermissions((prev) =>
        prev.filter((p) => !modulePermissions.find((mp) => mp.key === p))
      );
    } else {
      // Select all
      const newPermissions = modulePermissions.map((p) => p.key);
      setSelectedPermissions((prev) => [
        ...prev.filter((p) => !modulePermissions.find((mp) => mp.key === p)),
        ...newPermissions,
      ]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error(
        <p dir="rtl" className="font-custom">
          لطفا نام نقش را وارد کنید
        </p>
      );
      return;
    }

    try {
      setIsLoading(true);

      if (existingRole) {
        
        // Update existing role
        await rbacService.updateRole({
          RoleId: existingRole.id,
          ...formData,
        });
        // Update permissions
        await rbacService.assignPermissionsToRole(
          existingRole.id,
          selectedPermissions
        );

        toast.success(
          <p dir="rtl" className="font-custom">
            نقش با موفقیت ویرایش شد!
          </p>
        );
      } else {
        // Create new role
        const newRole = await rbacService.createRole(formData);
        // Assign permissions to the new role
        if (selectedPermissions.length > 0) {
          await rbacService.assignPermissionsToRole(
            newRole.data.id,
            selectedPermissions
          );
        }

        toast.success(
          <p dir="rtl" className="font-custom">
            نقش با موفقیت ایجاد شد!
          </p>
        );
      }

      setTimeout(() => {
        navigate("/dashboard/roles");
      }, 1000);
    } catch (error) {
      toast.error(
        <p dir="rtl" className="font-custom">
          خطا در {existingRole ? "ویرایش" : "ایجاد"} نقش!
        </p>
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (loadingPermissions || loadingRolePermissions) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-custom">در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="max-w-4xl w-full font-custom mx-auto bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-xl text-center font-semibold text-gray-700 mb-4">
          {existingRole ? "ویرایش نقش" : "ایجاد نقش جدید"}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col w-full text-right">
            <label htmlFor="name" className="text-sm text-gray-600 mb-1">
              نام نقش:
            </label>
            <input
              type="text"
              name="name"
              dir="rtl"
              value={formData.name}
              onChange={handleChange}
              className="w-full text-right p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div className="flex flex-col w-full text-right">
            <label htmlFor="description" className="text-sm text-gray-600 mb-1">
              توضیحات:
            </label>
            <textarea
              name="description"
              dir="rtl"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full text-right p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex flex-col w-full text-right">
            <label className="text-sm text-gray-600 mb-2">دسترسی‌ها:</label>

            <Collapse accordion>
              {Object.entries(PERMISSION_GROUPS).map(([module, permissions]) => {
                const allSelected = permissions.every((p) =>
                  selectedPermissions.includes(p.key)
                );

                return (
                  <Panel
                    header={
                      <div className="flex justify-between items-center font-custom">
                        <span>{PERMISSION_MODULE_NAMES[module]}</span>
                        <Checkbox
                          checked={allSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleModuleToggle(permissions);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          انتخاب همه
                        </Checkbox>
                      </div>
                    }
                    key={module}
                    className="font-custom"
                  >
                    <div className="space-y-2">
                      {permissions.map((permission) => (
                        <div key={permission.key} className="flex items-center">
                          <Checkbox
                            checked={selectedPermissions.includes(permission.key)}
                            onChange={() => handlePermissionToggle(permission.key)}
                          >
                            {permission.label}
                          </Checkbox>
                        </div>
                      ))}
                    </div>
                  </Panel>
                );
              })}
            </Collapse>
          </div>

          <div className="flex gap-3 justify-center mt-6">
            <button
              type="submit"
              disabled={isLoading}
              className="flex justify-center items-center gap-2 bg-blue-500 hover:bg-blue-700 text-white font-medium rounded-lg text-sm px-6 py-2.5 transition-colors duration-300 disabled:opacity-70"
            >
              {isLoading ? (
                <FaSpinner className="animate-spin" />
              ) : existingRole ? (
                "ویرایش نقش"
              ) : (
                "ایجاد نقش"
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate("/dashboard/roles")}
              className="bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg text-sm px-6 py-2.5 transition-colors"
            >
              انصراف
            </button>
          </div>
        </form>
      </div>

      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        rtl
        theme="dark"
        transition={Bounce}
      />
    </div>
  );
};

export default RoleForm;
